import { NavLink } from 'react-router-dom';
import { NAV_GROUPS, pagesInGroup, type NavPage } from '../app/navigation';
import { Icon } from './Icon';

/**
 * Sidebar: the plan's 11 pages, grouped by operator intent, with the page's capability key
 * (in the tooltip) and the honest page status (live / pending) visible on every entry.
 */
export function Sidebar({ pages }: { pages?: readonly NavPage[] }) {
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: (pages ? pages.filter((p) => p.group === group.id) : pagesInGroup(group.id)).sort(
      (a, b) => a.planPage - b.planPage,
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <nav
      className="flex h-full flex-col gap-4 overflow-y-auto border-e border-surface-line bg-surface-base px-2 py-3"
      aria-label="ناوبری اصلی"
      data-testid="sidebar"
    >
      <div className="flex items-center gap-2 px-1">
        <span className="flex size-8 items-center justify-center rounded-panel border border-accent/40 bg-accent-bg text-accent-ink">
          <Icon name="olt" size={17} />
        </span>
        <div className="leading-tight">
          <p className="text-ui font-bold text-ink">کنسول ACS</p>
          <p className="text-micro text-ink-faint">عملیات FTTX · I4</p>
        </div>
      </div>

      {groups.map((group) => (
        <div key={group.id}>
          <p className="px-2 pb-1 text-micro font-semibold uppercase tracking-wider text-ink-faint">
            {group.label}
          </p>
          <ul className="space-y-0.5">
            {group.items.map((page) => (
              <li key={page.id}>
                <SidebarLink page={page} />
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="mt-auto rounded-panel border border-surface-line bg-surface-raised p-2 text-caption text-ink-faint">
        <p>
          کنسول فقط مصرف‌کنندهٔ <span className="mono">/integration/v1</span> و{' '}
          <span className="mono">/ops/v1</span> است؛ دسترسی مستقیم به GenieACS (۷۵۷۵) وجود ندارد.
        </p>
      </div>
    </nav>
  );
}

function SidebarLink({ page }: { page: NavPage }) {
  const to = page.path ? `/${page.path}` : '/';
  return (
    <NavLink
      to={to}
      end={page.path === ''}
      className={({ isActive }) => `nav-item ${isActive ? 'nav-item-active' : ''}`}
      title={`${page.title} — کلید دسترسی: ${page.capability}`}
      data-page-id={page.id}
    >
      <Icon name={page.icon} size={15} />
      <span className="flex-1 truncate">{page.crumb}</span>
      {page.status === 'pending' ? (
        <span
          className="rounded-chip border border-surface-line px-1 text-micro text-ink-faint"
          data-testid={`nav-pending-${page.id}`}
        >
          {page.pendingReason === 'api' ? 'API بعدی' : 'UI بعدی'}
        </span>
      ) : (
        <span
          className="size-1.5 rounded-full bg-status-online"
          data-testid={`nav-live-${page.id}`}
        />
      )}
    </NavLink>
  );
}
